import React from 'react';
import styled from 'styled-components';
import { SkeletonButton } from '@ui/buttons/SkeletonButton';
import { TransparentFlatButton } from '@ui/buttons';
import { DOC_SELECT_MODES } from '@constants';
import { DocumentSelectionModal } from '@containers/DigitalAnalyst/components/DocumentSelectionModal';
import SelectedDocument from '@containers/DigitalAnalyst/components/SelectedDocument';
import { Field } from '@features/query/form/Field';
import { FormStateContext } from '@/contexts/FormStateContext';

const SelectedDocumentsWrapper = styled.div`
  max-height: 200px;
  min-height: 42px;
  overflow-y: auto;
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 4px;
  padding: 8px;
`;
const ChipsRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 8px;
`;
const ActionsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;

  padding-top: 16px;
`;

const CSV_MODES = [DOC_SELECT_MODES.userCsv];

const CsvDocsPicker = () => {
  const { state, setFormStateValue } = React.useContext(FormStateContext);
  const [isModalOpen, setIsModalOpen] = React.useState(false);

  const selectedCsvDocs = state.selectedCsvDocs || [];
  const invalidIds = state.invalidCsvDocIds || [];
  const errorByDocId = state.csvErrorByDocId || {};
  const invalidIdSet = React.useMemo(() => new Set(invalidIds), [invalidIds]);

  const handleAddDocument = React.useCallback(
    (doc) => {
      if (selectedCsvDocs.some((d) => d.id === doc.id)) return;
      setFormStateValue('selectedCsvDocs', [...selectedCsvDocs, doc]);
    },
    [selectedCsvDocs, setFormStateValue]
  );

  const removeDocsByIds = React.useCallback(
    (idSet) => {
      const remaining = selectedCsvDocs.filter((d) => !idSet.has(d.id));
      setFormStateValue('selectedCsvDocs', remaining);

      const nextInvalid = invalidIds.filter((id) => !idSet.has(id));
      if (nextInvalid.length !== invalidIds.length) {
        setFormStateValue('invalidCsvDocIds', nextInvalid);
      }
      const nextErrors = { ...errorByDocId };
      let mutated = false;
      idSet.forEach((id) => {
        if (id in nextErrors) {
          delete nextErrors[id];
          mutated = true;
        }
      });
      if (mutated) setFormStateValue('csvErrorByDocId', nextErrors);
    },
    [selectedCsvDocs, invalidIds, errorByDocId, setFormStateValue]
  );

  const handleRemoveByFilename = React.useCallback(
    (filename) => {
      const ids = new Set(selectedCsvDocs.filter((d) => d.filename === filename).map((d) => d.id));
      if (!ids.size) return;
      removeDocsByIds(ids);
    },
    [selectedCsvDocs, removeDocsByIds]
  );

  const handleRemoveDocument = React.useCallback(
    (doc) => {
      if (!doc || doc.id == null) return;
      removeDocsByIds(new Set([doc.id]));
    },
    [removeDocsByIds]
  );

  const handleRemoveAll = React.useCallback(() => {
    if (!selectedCsvDocs.length) return;
    removeDocsByIds(new Set(selectedCsvDocs.map((d) => d.id)));
  }, [selectedCsvDocs, removeDocsByIds]);

  return (
    <div>
      {isModalOpen ? (
        <DocumentSelectionModal
          onDismiss={() => setIsModalOpen(false)}
          docSelectModes={CSV_MODES}
          docs={[]}
          isLoadingDocs={false}
          tickers={[]}
          selectedDocs={selectedCsvDocs}
          handleAddDocument={handleAddDocument}
          handleAddDocumentSpeakerSearch={handleAddDocument}
          handleRemoveDocument={handleRemoveDocument}
        />
      ) : null}
      {selectedCsvDocs.length > 0 && (
        <SelectedDocumentsWrapper>
          <ChipsRow>
            {selectedCsvDocs.map((doc) => {
              const invalid = invalidIdSet.has(doc.id);
              return (
                <SelectedDocument
                  key={doc.id}
                  doc={doc}
                  handleRemoveClick={handleRemoveByFilename}
                  selectedDocs={selectedCsvDocs}
                  invalid={invalid}
                  invalidReason={invalid ? errorByDocId[doc.id] : undefined}
                />
              );
            })}
          </ChipsRow>
        </SelectedDocumentsWrapper>
      )}
      <ActionsWrapper>
        <SkeletonButton onClick={() => setIsModalOpen(true)}>Select CSV Data</SkeletonButton>
        {selectedCsvDocs.length > 0 && (
          <TransparentFlatButton onClick={handleRemoveAll}>Remove All</TransparentFlatButton>
        )}
      </ActionsWrapper>
    </div>
  );
};

export const CsvDocAttachment = () => {
  return <Field label="CSV Data" component={<CsvDocsPicker />} />;
};
